import { z } from 'zod';
import { EventoFile } from './enums';
import { dataOraSchema, idSchema } from '../common';

/**
 * Archivio documentale: blocco → cartelle (max 1 livello) → slot → file.
 * Lo slot esiste sempre, anche vuoto: è il "documento atteso".
 * Un blocco appartiene a UNO SOLO tra progetto / fase / cliente (XOR, CHECK a DB).
 */

export const bloccoFileCreateSchema = z
  .object({
    nome: z.string().trim().min(1).max(255),
    progettoId: idSchema.optional(),
    faseProgettoId: idSchema.optional(),
    clienteId: idSchema.optional(),
  })
  .superRefine((b, ctx) => {
    const ancore = [b.progettoId, b.faseProgettoId, b.clienteId].filter((v) => v != null);
    if (ancore.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Un blocco file appartiene a uno solo tra progetto, fase e cliente',
        path: ['progettoId'],
      });
    }
  });
export type BloccoFileCreate = z.infer<typeof bloccoFileCreateSchema>;

export const cartellaCreateSchema = z.object({
  bloccoFileId: idSchema,
  nome: z.string().trim().min(1).max(255),
  ordine: z.coerce.number().int().min(0).default(0),
});
export type CartellaCreate = z.infer<typeof cartellaCreateSchema>;

/**
 * Base SENZA il refine XOR: Zod non permette `.pick()`/`.omit()` su uno
 * schema con `.superRefine()` applicato — questa resta `.pick()`-abile per
 * i DTO di rotte annidate dove l'ancora arriva dal path, non dal body
 * (es. `apps/api/src/file/dto.ts`).
 */
export const fileSlotBaseSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  bloccoFileId: idSchema.optional(),
  cartellaId: idSchema.optional(),
  obbligatorio: z.boolean().default(false),
  ordine: z.coerce.number().int().min(0).default(0),
});

/** Uno slot sta in una cartella OPPURE direttamente nel blocco (XOR). */
export const fileSlotCreateSchema = fileSlotBaseSchema.superRefine((s, ctx) => {
  if ((s.bloccoFileId == null) === (s.cartellaId == null)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Lo slot sta in una cartella oppure direttamente nel blocco, non entrambi',
      path: ['cartellaId'],
    });
  }
});
export type FileSlotCreate = z.infer<typeof fileSlotCreateSchema>;

/**
 * Il file dentro uno slot pieno. `contentType: 'image/jpeg'` identifica
 * sempre una foto già normalizzata — nessun altro formato
 * della categoria "foto" viene mai persistito così com'è, e la categoria
 * "documento" non produce mai questo content-type: quando è presente, esiste
 * sempre anche una miniatura alla chiave derivata.
 */
export const fileCaricatoSchema = z.object({
  id: idSchema,
  nomeFile: z.string(),
  dimensione: z.number().int().min(0),
  contentType: z.string(),
  caricatoDa: idSchema.nullable(),
  caricatoIl: dataOraSchema,
});
export type FileCaricato = z.infer<typeof fileCaricatoSchema>;

export const fileSlotSchema = z.object({
  id: idSchema,
  nome: z.string(),
  bloccoFileId: idSchema.nullable(),
  cartellaId: idSchema.nullable(),
  obbligatorio: z.boolean(),
  ordine: z.number().int(),
  /** Generato al volo da un upload senza slot: alla cancellazione del file cade anche lo slot. */
  generatoDaUpload: z.boolean(),
  file: fileCaricatoSchema.nullable(),
});
export type FileSlotVista = z.infer<typeof fileSlotSchema>;

/**
 * Cartella con i suoi slot diretti (max un livello) — vista annidata
 * dentro `bloccoFileVistaSchema`, non un endpoint a sé.
 */
export const cartellaVistaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  ordine: z.number().int(),
  slot: z.array(fileSlotSchema),
});
export type CartellaVista = z.infer<typeof cartellaVistaSchema>;

/** L'albero di UN blocco: progetto, una fase, o il cliente. */
export const bloccoFileVistaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cartelle: z.array(cartellaVistaSchema),
  /** Slot direttamente nel blocco, senza cartella. */
  slot: z.array(fileSlotSchema),
});
export type BloccoFileVista = z.infer<typeof bloccoFileVistaSchema>;

/**
 * Una voce della navigazione primaria: il blocco di progetto o una
 * fase, col contatore `caricati/totale` usato sia come indicatore rapido sia
 * per evidenziare gli obbligatori mancanti. Evita di scaricare l'albero
 * completo di ogni fase solo per popolare la sidebar.
 */
export const fileRiepilogoVoceSchema = z.object({
  /** `null` = riga del blocco di progetto, non di una fase. */
  faseProgettoId: idSchema.nullable(),
  nome: z.string(),
  slotCaricati: z.number().int().min(0),
  slotTotali: z.number().int().min(0),
  slotObbligatoriMancanti: z.number().int().min(0),
});
export type FileRiepilogoVoce = z.infer<typeof fileRiepilogoVoceSchema>;

/**
 * L'upload passa dall'API (proxy sincrono: valida, normalizza se foto, scrive
 * su S3), non da un presigned diretto: qui non serve uno schema
 * di richiesta, arriva come `multipart/form-data`. Resta uno schema per
 * **leggere** il file originale, sempre via URL firmato a scadenza breve, mai
 * pubblico — l'anteprima invece non ne ha bisogno: è uno stream
 * diretto della miniatura, proxato come l'immagine profilo.
 */
export const urlFirmatoSchema = z.object({
  url: z.url(),
  scadeIl: dataOraSchema,
});
export type UrlFirmato = z.infer<typeof urlFirmatoSchema>;

/**
 * `inline`: chiede una disposizione `inline` invece di `attachment`, così il
 * browser visualizza il file (immagini, PDF) invece di scaricarlo — il tasto
 * "Apri" dell'archivio, non "Scarica".
 */
export const urlDownloadQuerySchema = z.object({
  inline: z.coerce.boolean().optional().default(false),
});
export type UrlDownloadQuery = z.infer<typeof urlDownloadQuerySchema>;

export const rinominaFileSchema = z.object({
  nomeFile: z.string().trim().min(1).max(255),
});
export type RinominaFile = z.infer<typeof rinominaFileSchema>;

/** `file_log` è append-only: ogni evento ha autore e timestamp. */
export const fileLogSchema = z.object({
  id: idSchema,
  /** Nullable: sopravvive anche alla cancellazione dello slot. */
  fileSlotId: idSchema.nullable(),
  fileCaricatoId: idSchema.nullable(),
  evento: EventoFile,
  nomeFile: z.string().nullable(),
  utenteId: idSchema.nullable(),
  /** Nome completo risolto dal join, per la timeline "Get Info" — null se l'utente è stato disattivato/rimosso. */
  autore: z.string().nullable(),
  creatoIl: dataOraSchema,
});
export type FileLog = z.infer<typeof fileLogSchema>;
